import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Feed, FeedCategory, FeedSummary } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { orAbsent, relativeDate } from "../lib/format";
import { Spinner } from "../components/Spinner";

/* The feeds the engine watches, and what each one produces.
 *
 * James, 2026-09-22: a list of sources, the ability to add one, stats on the
 * feeds, the list organised into categories, with duds hidden by default.
 *
 * The Ingestion screen shows what arrived. This shows where it comes from, and
 * it is the screen that answers whether a feed is worth polling.
 *
 * WHY DUDS ARE HIDDEN BY DEFAULT
 *
 * 6,057 feeds exist and 1,195 have ever put an item into the pipeline. The
 * rest are mostly candidates the discovery sweep found and never proved. A
 * list showing all of them buries the ones that work. api.feed computes
 * `productive` so the rule has one home, and the filter here reads that column
 * rather than restating it.
 *
 * WHY KEEP RATE IS ON EVERY ROW
 *
 * Items kept over items seen. It is the number that says whether a feed earns
 * its polling budget. cre-news-regional polls as much as aec-trade and keeps a
 * twentieth as much, and no count of items on its own shows that.
 */

const FILTERS = ["category", "status", "shown"] as const;

const CSV_DEFAULT = ["name", "feed_url", "category", "status", "items_seen",
                     "items_kept", "keep_rate_pct", "last_polled_at",
                     "last_item_at"] as const;

/* What api.add_feed returns. It is a set-returning function, so PostgREST
   sends an array of one row. */
type AddFeedResult = { id: string; name: string; status: string; added: boolean };

function num(v: number | null | undefined) {
  return (v ?? 0).toLocaleString();
}

/* The header: what exists, what works, what is broken.
 *
 * A failed header must not take the list with it, so an error here renders
 * nothing and the list still loads. */
function Overview({ token, onPickCategory, picked }:
                  { token: string; onPickCategory: (c: string) => void; picked: string }) {
  const summary = useQuery({
    queryKey: ["feed-summary"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("feed_summary").select("*").limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as FeedSummary | null;
    },
  });

  /* Six rows, grouped by api.feed_category rather than here. Grouping 6,057
     feeds in the browser is an unbounded fetch. */
  const categories = useQuery({
    queryKey: ["feed-categories"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("feed_category")
        .select("category,feeds,productive,active,failing,items_seen,items_kept,keep_rate_pct,last_polled_at")
        .order("feeds", { ascending: false })
        .limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as FeedCategory[];
    },
  });

  const d = summary.data;
  const cats = categories.data ?? [];

  return (
    <>
      {d ? (
        <div className="queue">
          <span><strong>{num(d.feeds)}</strong> feeds</span>
          <span><strong>{num(d.productive)}</strong> produce records</span>
          <span><strong>{num(d.active)}</strong> active, {num(d.candidate)} candidate,{" "}
            {num(d.paused)} paused, {num(d.dead)} dead</span>
          {(d.failing ?? 0) > 0
            ? <span className="refusal"><strong>{num(d.failing)}</strong> failing</span>
            : null}
          <span className="muted">{num(d.items_kept)} items kept of {num(d.items_seen)} seen</span>
          <span className="muted">{num(d.polled_24h)} polled in the last 24 hours</span>
        </div>
      ) : null}

      {cats.length ? (
        <table className="tight">
          <thead>
            <tr>
              <th>Category</th>
              <th className="num">Feeds</th>
              <th className="num">Producing</th>
              <th className="num">Failing</th>
              <th className="num">Items seen</th>
              <th className="num">Items kept</th>
              <th className="num">Keep rate</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => {
              const name = c.category ?? "";
              const on = picked === name;
              return (
                <tr key={name}>
                  <td>
                    {/* Clicking a category narrows the list below. The list
                        stays paged, so this filters rather than expands. */}
                    <button className="link" aria-pressed={on}
                            onClick={() => onPickCategory(on ? "" : name)}>
                      {on ? <strong>{name}</strong> : name}
                    </button>
                  </td>
                  <td className="num">{num(c.feeds)}</td>
                  <td className="num">{num(c.productive)}</td>
                  <td className="num">{(c.failing ?? 0) > 0
                    ? <span className="refusal">{num(c.failing)}</span>
                    : "0"}</td>
                  <td className="num">{num(c.items_seen)}</td>
                  <td className="num"><strong>{num(c.items_kept)}</strong></td>
                  <td className="num">{c.keep_rate_pct === null || c.keep_rate_pct === undefined
                    ? orAbsent(null) : `${c.keep_rate_pct}%`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

/* Add a feed.
 *
 * It lands as a candidate, which api.add_feed decides rather than this form.
 * Whether a URL is really a feed and whether anything on it is relevant are
 * things only a poll can establish. */
function AddFeed({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [said, setSaid] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: () =>
      callFunction<AddFeedResult[]>(token, "add_feed", {
        p_feed_url: url.trim(),
        p_name: name.trim() || undefined,
        p_category: category.trim() || undefined,
      }),
    /* The function says which of the two happened, so this does not guess.
       Saying "added" over a URL already being polled is a small lie that
       costs somebody a second look. */
    onSuccess: (rows) => {
      const r = rows?.[0];
      setSaid(
        !r ? "Saved."
        : r.added ? `Added ${r.name}. It is a candidate until a poll proves it.`
        : `Already watched: ${r.name}, ${r.status}.`);
      setUrl("");
      setName("");
      queryClient.invalidateQueries({ queryKey: ["feeds"] });
      queryClient.invalidateQueries({ queryKey: ["feed-summary"] });
      queryClient.invalidateQueries({ queryKey: ["feed-categories"] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (!open) {
    return (
      <p>
        <button onClick={() => setOpen(true)}>Add a feed</button>
      </p>
    );
  }

  return (
    <div className="bar">
      <label htmlFor="nf-url">Feed URL</label>
      <input id="nf-url" value={url} style={{ width: "22rem" }}
             onChange={(e) => { setUrl(e.target.value); setSaid(null); }} />
      <label htmlFor="nf-name">Name</label>
      <input id="nf-name" value={name} style={{ width: "12rem" }}
             onChange={(e) => { setName(e.target.value); setSaid(null); }} />
      <label htmlFor="nf-cat">Category</label>
      <input id="nf-cat" value={category} style={{ width: "10rem" }}
             onChange={(e) => { setCategory(e.target.value); setSaid(null); }} />
      <button disabled={url.trim().length < 8 || add.isPending}
              onClick={() => add.mutate()}>
        {add.isPending ? <><Spinner />Adding</> : "Add"}
      </button>
      <button onClick={() => { setOpen(false); setSaid(null); }}>Cancel</button>
      {said ? <span className={add.isError ? "refusal" : "muted"}>{said}</span> : null}
    </div>
  );
}

export function Sources({ session }: { session: SignedIn }) {
  const params = useListParams("items_kept", FILTERS, false, "feed");
  const csvColumns = useCsvColumns("sources", "feed", CSV_DEFAULT);
  const queryClient = useQueryClient();

  /* Absent means productive only, so the default state is the empty URL and a
     shared link says what it shows. */
  const showAll = params.filters["shown"] === "all";

  const narrowedFor = (columns: string) => {
    let q = apiClient(session.idToken).from("feed").select(columns, { count: "exact" });
    if (!showAll) q = q.eq("productive", "true");
    if (params.filters["category"]) q = q.eq("category", params.filters["category"]);
    if (params.filters["status"]) q = q.eq("status", params.filters["status"]);
    if (params.search.trim()) q = q.ilike("name", `*${params.search.trim()}*`);
    return q;
  };

  const LIST_COLUMNS =
    "id,name,feed_url,category,status,items_seen,items_kept,keep_rate_pct," +
    "last_polled_at,last_item_at,consecutive_failures,last_status,last_error," +
    "productive,due_now";

  const query = useQuery({
    queryKey: ["feeds", params.page, params.sort, params.ascending,
               params.filters, params.search],
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowedFor(LIST_COLUMNS)
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as Feed[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];

  return (
    <ListShell
      title="Sources"
      view="feed"
      searchLabel="Feed name"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["feeds"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "feeds",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await narrowedFor(csvColumns.chosen.join(","))
              .order(params.sort, { ascending: params.ascending, nullsFirst: false })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as Feed[];
          },
        })
      }
      emptyEverything="No feeds match. Set Show to All feeds to include the rest."
      beforeList={
        <>
          <Overview
            token={session.idToken}
            picked={params.filters["category"] ?? ""}
            onPickCategory={(c) => params.set({
              page: 1, filters: { ...params.filters, category: c } })}
          />
          <AddFeed token={session.idToken} />
        </>
      }
      filters={
        <>
          <label htmlFor="f-status">Status</label>
          <select id="f-status" value={params.filters["status"] ?? ""}
                  onChange={(e) => params.set({
                    page: 1, filters: { ...params.filters, status: e.target.value } })}>
            <option value="">any</option>
            <option value="active">active</option>
            <option value="candidate">candidate</option>
            <option value="paused">paused</option>
            <option value="dead">dead</option>
          </select>
          <label htmlFor="f-shown">Show</label>
          <select id="f-shown" value={params.filters["shown"] ?? ""}
                  onChange={(e) => params.set({
                    page: 1, filters: { ...params.filters, shown: e.target.value } })}>
            <option value="">Feeds that produce records</option>
            <option value="all">All feeds</option>
          </select>
        </>
      }
    >
      <table>
        <thead>
          <tr>
            <SortHeader column="name" label="Feed" params={params} />
            <SortHeader column="category" label="Category" params={params} />
            <SortHeader column="status" label="Status" params={params} />
            <SortHeader column="last_polled_at" label="Last polled" params={params} />
            <SortHeader column="last_item_at" label="Last item" params={params} />
            <SortHeader column="items_seen" label="Items seen" params={params} numeric />
            <SortHeader column="items_kept" label="Items kept" params={params} numeric />
            <SortHeader column="keep_rate_pct" label="Keep rate" params={params} numeric />
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const failing = (f.consecutive_failures ?? 0) > 2;
            return (
              <tr key={f.id}>
                <td>
                  {orAbsent(f.name)}
                  {f.feed_url ? <div className="muted url">{f.feed_url}</div> : null}
                  {/* The error is on the row that has it. A feed failing for
                      days otherwise looks the same as one nobody polls. */}
                  {failing ? (
                    <div className="refusal">
                      Failed {f.consecutive_failures} times.{" "}
                      {f.last_error ? f.last_error.slice(0, 80) : f.last_status}
                    </div>
                  ) : null}
                </td>
                <td className="muted nowrap">{orAbsent(f.category)}</td>
                <td className="muted">{orAbsent(f.status)}</td>
                <td className="muted">{relativeDate(f.last_polled_at)}</td>
                <td className="muted">{relativeDate(f.last_item_at)}</td>
                <td className="num">{num(f.items_seen)}</td>
                <td className="num"><strong>{num(f.items_kept)}</strong></td>
                <td className="num">
                  {f.keep_rate_pct === null || f.keep_rate_pct === undefined
                    ? orAbsent(null) : `${f.keep_rate_pct}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ListShell>
  );
}
